import React, { useState, useEffect, useRef, useContext, useCallback } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import AppBarUI from '@material-ui/core/AppBar'
import Drawer from '@material-ui/core/Drawer'
import Hidden from '@material-ui/core/Hidden'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import Link from '@material-ui/core/Link'
import Toolbar from '@material-ui/core/Toolbar'
import Chip from '@material-ui/core/Chip'
import { Modal } from '@material-ui/core'
import { window, document } from 'browser-monads'
import axios from 'axios'
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination } from 'swiper';

import { EqualHeight, EqualHeightElement } from '../components/EqualHeight'
import Announcement from '../components/Announcement'
import Text from '../components/Text'
import Icon from '../components/Icon'
import LoginButton from '../components/LoginButton'
import Logo from '../components/Logo'
import ChooseCountryOverlay from '../components/ChooseCountryOverlay'
import { GTMEvent } from '../utils/gtm'
import { detectAlpha2Country } from '@src/utils/country'
import cookie from '@src/utils/cookie'
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from '../context/GlobalContextProvider'
import { useTranslation } from 'react-i18next'
import { getKeys } from '@src/utils/dictionary'

import 'swiper/swiper-bundle.css'

// Newsletter popup images
import newspopDesktop from './../assets/img/Fanhome_Popup_Newsletter_DESKTOP.png'
import newspopMobile from './../assets/img/Fanhome_Popup_Newsletter_MOBILE.png'

// Import for CardProductV2 - using existing component
import CardProductV2 from '../components/CardProductv2'

import '../style/SnakeCommerceCart.css'
import FluidImage from '../components/FluidImage';
import InfiniteScroll from '../components/InfiniteScroll';
import NewsletterSubscriptionModule from './NewsletterSubscriptionModule';

SwiperCore.use([Navigation, Pagination]);

const useStyles = makeStyles(theme => ({
  differentCountry: {
    width: '100%',
    backgroundColor: theme.customPalette.darkishPurple,
    fontFamily: 'Spezia, \'Roboto\', \'Open Sans\', Helvetica, Arial, sans-serif',
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 'normal',
    padding: 8,
    color: '#ffffff',
    zIndex: '1001',
    position: 'relative',
    '& br': {
      display: 'none',
      [theme.breakpoints.only('xs')]: {
        display: 'flex',
      },
    },
  },
  appBar: {
    padding: theme.spacing(1, 2, 1, 2),
    backgroundColor: 'transparent',
    color: '#000',
    boxShadow: 'none',
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(3, 2, 3, 2),
    },
    top: 0,
    zIndex: 1000,
  },
  appBarTop: {
    marginTop: 30,
    [theme.breakpoints.only('sm')]: {
      marginTop: 35,
    },
    [theme.breakpoints.only('xs')]: {
      marginTop: 50,
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 0,
    position: 'relative',
    '& button': {
      width: 'auto !important',
      height: 'auto !important',
      boxShadow: 'unset !important',
    },
  },
  toolbarMarginTop: {
    paddingTop: 36,
  },
  announcement: {
    position: 'relative',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    zIndex: 1001,
    background: '#8c4dea'
  },
  logo: {
    display: 'flex',
    flexGrow: 1,
    '& a': {
      height: 56,
      maxWidth: '184px',
      width: '100%',
      cursor: 'pointer',
    },
    [theme.breakpoints.only('sm')]: {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100%',
      maxWidth: '200px',
    },
    [theme.breakpoints.only('xs')]: {
      maxWidth: '184px',
      height: 43,
      '& a': {
        height: 43,
        maxWidth: '184px',
      },
    },
  },
  icon: {
    height: 32,
  },
  announcementSpacer: {},
  sticky: {
    position: 'fixed',
    zIndex: 1000,
    background: 'white',
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(1, 2, 1, 2),
    },
    outline: '3px solid rgba(0,0,0,0.03)',
    '& $announcementSpacer': {
      display: 'none',
    },
  },
  stickyBlack: {
    position: 'fixed',
    zIndex: 1000,
    background: '#1f1f1f',
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(1, 2, 1, 2),
    },
    outline: '3px solid rgba(0,0,0,0.03)',
    '& $announcementSpacer': {
      display: 'none',
    },
    '& $menuCategory': {
      color: '#fff',
    },
  },
  drawer: {
    width: '100%',
    flexShrink: 0,
  },
  drawerPaper: {
    width: '100%',
  },
  drawerHeader: {
    borderTopColor: '#ffffff',
    borderBottom: 'none',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    margin: '40px 80px 0',
    ...theme.mixins.toolbar,
    justifyContent: 'center',
    minHeight: 'auto',
    marginBottom: theme.spacing(6),
    [theme.breakpoints.only('xs')]: {
      margin: '20px 16px',
      justifyContent: 'space-between',
      paddingRight: 0,
    },
    '& $logo': {
      position: 'relative',
      left: 'unset',
      transform: 'unset',
      maxWidth: '260px',
      '& a': {
        maxWidth: '260px',
        backgroundPosition: 'left',
      },
    },
    '& $closeIcon': {
      height: 16,
    },
  },
  closeIcon: {
    height: 48,
    [theme.breakpoints.only('xs')]: {
      height: 32,
    },
  },
  closeButton: {
    padding: 0,
    width: 'auto !important',
    height: 'auto !important',
    boxShadow: 'unset !important',
  },
  list: {
    [theme.breakpoints.up('sm')]: {
      margin: '0 80px',
    },
  },
  listItem: {
    justifyContent: 'flex-start',
    margin: 0,
    padding: theme.spacing(4, 0),
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(3, 2),
    },
  },
  pageLink: {
    color: '#000',
    textDecoration: 'none',
    '&:visited': {
      color: '#000',
    },
    '&:hover': {
      color: theme.customPalette.tangerine,
      textDecoration: 'none',
    },
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    textAlign: 'left',
    alignItems: 'center',
    fontFamily: 'Spezia',
    lineHeight: 1.17,
    fontWeight: '400',
    fontSize: 24,
    [theme.breakpoints.only('xs')]: {
      fontSize: 16,
    },
  },
  menuCategory: {
    marginRight: theme.spacing(3),
    fontWeight: '600 !important',
    fontSize: '14px !important',
  },
  desktopRightSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textTransform: 'uppercase',
  },
  iconSearch: {
    width: 24,
    marginRight: 16,
    cursor: 'pointer',
  },
  iconSearchMobile: {
    position: 'relative',
    cursor: 'pointer',
    width: 24,
    alignSelf: 'center',
    [theme.breakpoints.only('sm')]: {
      position: 'absolute',
      left: '60vw',
    },
    [theme.breakpoints.only('xs')]: {
      position: 'absolute',
      left: '70vw',
    },
  },
  bgGrey: {
    backgroundColor: '#fbfbfb',
  },
  newsletterPopupContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  newsPopContent: {
    backgroundColor: '#f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: '100%',
    position: 'relative',
    overflow: 'scroll',
    width: '100vw',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      width: '80vw',
      height: 'auto',
      justifyContent: 'center',
    },
  },
  newspopMobile: {
    display: 'block',
    objectFit: 'contain',
    width: '100%',
    height: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  newspopDesktop: {
    display: 'none',
    objectFit: 'contain',
    width: '100%',
    height: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  iconClosePop: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 16,
    height: 16,
  },
  menuDesktop: {
    position: 'absolute',
    top: '100%',
    width: '100%',
    boxSizing: 'border-box',
    zIndex: 30,
  },
  headerMenuDesktop: {
    position: 'absolute',
    bottom: 0,
    left: -16,
    boxSizing: 'border-box',
    transform: 'translateY(100%)',
    width: '100%',
    opacity: 1,
    transition: 'opacity 0.3s ease-in-out',
    zIndex: 1000,
    backgroundColor: '#fbfbfb',
    boxShadow: '0px 3px 16px 0px #00000029'

  },
  headerMenuDesktopOpen: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  headerMenuDesktopClose: {
    opacity: 0,
    pointerEvents: 'none',
  },
  dropdownSelectorTriangle: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateY(-98%) translateX(-50%)',
    width: 16,
    height: 16,
    clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
    opacity: 1,
    zIndex: 1,
    backgroundColor: '#fbfbfb',
    animation: '$exa 0.8s ease-out',
  },
  '@keyframes exa': {
    '0%': {
      opacity: 0
    },
    '100%': {
      opacity: 1
    },
  },
  filterContainer: {
    overflowX: 'auto',
    display: 'flex',
    padding: '16px 0',
    marginBottom: 24,
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  filterLabel: {
    padding: '8px 16px',
    backgroundColor: '#fbfbfb',
    border: '1px solid #f29100',
    borderRadius: 30,
    marginRight: 8,
    fontSize: 16,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  filterLabelChecked: {
    backgroundColor: '#f29100',
    color: '#ffffff',
  },
  breadcrumbs: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    marginTop: 10,
    position: 'absolute',
    top: '100%',
    left: 32,
    zIndex: 29,
    '& > span': {
      display: 'flex',
      alignItems: 'center',
      fontSize: 14,
      color: '#FFFFFF',
      '&:not(:last-child)': {
        marginRight: 8,
        '&:after': {
          content: '\' > \'',
          marginLeft: 8,
        },
      },
      '&:last-child': {
        fontWeight: 'bold',
      },
    },
  },
  search_section_mobile: {
    position: 'absolute',
    opacity: '0',
    backgroundColor: '#fff',
    zIndex: -1,
    display: 'flex',
    alignItems: 'center',
  },
  search_section_mobile_active: {
    left: '0',
    marginLeft: '-16px',
    width: 'calc(100% + 32px)',
    opacity: '1',
    visibility: 'visible',
    display: 'flex',
    position: 'absolute',
    height: '100%',
    top: '-8px',
    backgroundColor: '#fff',
    zIndex: '2',
    alignItems: 'center',
    transition: 'all 0.45s ease-in',
  },
  search_input: {
    fontFamily: 'Poppins',
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    border: '0px',
    fontSize: '20px',
    boxShadow: 'none',
    outline: 'none',
    [theme.breakpoints.down('md')]: {
      fontSize: '16px',
    },
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'transparent',
    zIndex: 1,
  },
  megaMenuContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: 1400,
    width: '100%',
    height: 'auto',
    margin: '0 auto',
    boxSizing: 'border-box'
  },
  megaMenuWrapper: {
    display: 'flex',
    width: '100%',
    paddingRight: 32,
    paddingLeft: 32,
    paddingTop: 30,
    paddingBottom: 30,
  },
  megaMenuListContainer: {
    maxWidth: 200,
    width: '100%',
  },
  megaMenuListWrapper: {
    display: 'flex',
    flexDirection: 'column',
    paddingRight: '24px',
    paddingLeft: '24px',
    height: '100%',
    boxSizing: 'border-box'
  },
  menuProductContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
    overflow: 'hidden',
    width: '100%',
  },
  menuProductWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    gap: 10,
  },
  recommendationCardContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 391.11,
    height: '100%',
    position: 'relative',
    marginLeft: 'auto',
    marginRight: 30,
  },
  recommendationCardLink: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    cursor: 'pointer',
  },
  recommendationCardHeader: {
    height: 220,
    position: 'relative',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
    }
  },
  imgBackground: {
    width: '100%',
    height: '100%',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    }
  },
  imgForeground: {
    position: 'absolute',
    top: '7%',
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    }
  },
  imgForegroundSingle: {
    height: '100% !important',
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'cover',
    }
  },
  recommendationCardBody: {
    marginTop: 10,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Spezia'
  },
  productPriceReference: {
    fontWeight: 500,
    fontSize: 14,
    color: '#9D75B1'
  },
  productTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: '#662482',
    paddingTop: 4
  },
  menuProductItem: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 210
  },
  headerCard: {
    position: 'relative',
    height: 230,
    width: '100%',
    backgroundColor: '#f5f5f5',
    overflow: 'hidden',
  },
  headerCardContent: {
    zIndex: 2,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
  },
  cardTextDescription: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '140%',
    fontFamily: 'Spezia',
    color: '#581D70',
    marginTop: 16,
  },
  imgContainer: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    '& img': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      objectPosition: 'top',
    },
  },
  cardLink: {
    width: '100%',
    height: '100%',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  cardLogo: {
    maxWidth: '90%',
    width: '100%',
    alignSelf: 'center',
    objectFit: 'cover',
    margin: 'auto',
    position: 'relative',
    zIndex: 1
  },
  cardChip: {
    fontFamily: 'Poppins',
    marginTop: 0,
    fontWeight: 400,
    width: 'fit-content',
    textTransform: 'uppercase',
    padding: '2px 4px',
    height: 25,
    position: 'relative',
    zIndex: 1,
    '& span': {
      padding: 0,
    },
  },
  megaMenuFooter: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: '16px 0px',
    textAlign: 'center',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '50%',
      right: '50%',
      marginLeft: '-50vw',
      marginRight: '-50vw',
      top: 0,
      borderTop: '1px solid rgba(0, 0, 0, 0.1)',
      width: '100vw',
    },
  },
  megaMenuFooterLink: {
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    margin: '0 auto',
    gap: 12,
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 700,
    color: '#F29100',
    '&:hover': {
      color: theme.customPalette.tangerine,
      textDecoration: 'underline',
    },
  },
  packCardArrow: {
    width: 16,
    height: 16,
  },
  fieldMenuDesktop: {
    fontFamily: 'Spezia',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '150%',
    color: '#804996',
    textDecoration: 'none',
    padding: '8px 10px'
  },
  ctaDesktop: {
    fontSize: 20,
    fontWeight: 'bold',
    lineHeight: 1.1,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#f29100',
    marginBottom: 16,
    '&:hover': {
      color: '#f29100',
    },
  },
  containerCategory: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    cursor: 'pointer',
  },
  categoryLink: {
    fontFamily: 'Spezia',
    fontSize: 24,
    fontWeight: 400,
    lineHeight: 1.17,
    color: '#000',
    [theme.breakpoints.only('xs')]: {
      fontSize: 16,
    },
  },
  goIcon: {
    transition: 'transform 0.3s ease',
  },
  nestedListItem: {
    padding: theme.spacing(2, 0, 2, 2),
    marginLeft: theme.spacing(2),
  },
  listItemWrapper: {
    '& li a p': {
      fontSize: '16px !important',
      color: '#000',
    },
    '& li:last-child a': {
      '& p': {
        color: '#3b4154',
      },
      textDecoration: 'underline',
    }
  },
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    position: 'relative'

  },
  megaMenuSwiper: {
    paddingBottom: theme.spacing(4),
  },
  cardAndRecommendationContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    width: '100%',
    height: '100%',
  },
  megaMenuPagination: {
    textAlign: 'center',
    marginTop: '-20px',
    marginBottom: '16px',
    '& .swiper-pagination-bullet': {
      width: 8,
      height: 8,
      display: 'inline-block',
      borderRadius: '50%',
      background: theme.palette.text.primary,
      opacity: .2,
      margin: '0 4px',
      cursor: 'pointer',
    },
    '& .swiper-pagination-bullet-active': {
      opacity: 1,
      background: theme.customPalette.tangerine,
    },
  },
}));

const HeaderV2Module = props => {
  const {info, repo, templateLabel} = props
  const classes = useStyles()
  const {t} = useTranslation()
  const dispatch = useContext(GlobalDispatchContext)
  const {newsletter} = useContext(GlobalStateContext)

  // Refs for header height calculations
  const barRef = useRef(null)
  const topbarRef = useRef(null)
  const differentCountryRef = useRef(null)
  const navbarRef = useRef(null)
  const menuRefs = useRef({})

  // State variables
  const [sticky, setSticky] = useState(false)
  const [differentCountry, setDifferentCountry] = useState(false)
  const [showOverlay, setShowOverlay] = useState(false)
  const [countrySelected, setCountrySelected] = useState('')
  const [allCountries, setAllCountries] = useState([])

  const [searchMobileIsOpen, setSearchMobile] = useState(false)
  const [openSearch, setOpenSearch] = useState(false)
  const [serverProducts, setServerProducts] = useState([])
  const [products, setProducts] = useState([])
  const [text, setText] = useState('')
  const [tags, setTags] = useState([])
  const [tagSelected, setTagSelected] = useState('')
  const [wrapper, setWrapper] = useState({})

  const [isMenuMobileOpened, setMenuMobile] = useState(false)
  const [category, setCategory] = useState('')
  const [isCategoryOpen, setIsCategoryOpen] = useState({
    open: false,
    index: null,
  })
  const [heightCategory, setHeightCategory] = useState(0)

  // Newsletter modal states
  const [modalIsOpen, setIsOpen] = useState(false)
  const [news, setNews] = useState({})
  const [openItemsMenu, setOpenItemsMenu] = useState([])
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [productsPagination, setProductsPagination] = useState({
    page: 1,
    has_more_pages: false,
    total_pages: 1
  });

  // Extract header elements from props
  const getElementProp = type => {
    let content = null
    let arr = []
    for (let key in props) {
      if (
        props[key] != null &&
        typeof props[key] === 'object' &&
        props[key].type === type
      ) {
        content = props[key].content || []
        arr.push(...content)
      }
    }
    return arr || []
  }

  const HeaderAnnouncement = getElementProp('HeaderAnnouncement')
  const HeaderMenuItem = getElementProp('HeaderMenuItem')
  const HeaderServices = getElementProp('HeaderServices')
  const breadcrumbs = props?.repo?.seo?.breadcrumbs;

  const ProductCard = ({prod}) => (
    <div className={classes.menuProductItem}>
      <Link
        {...prod?.field_value?.product?.field_value?.menu_cta?.field_value}
        type={prod?.field_value?.product?.field_value?.menu_cta?.field_type}
        product_code={prod?.field_value?.product?.field_value?.menu_cta?.field_value?.product_code}
        repo={repo}
        className={classes.cardLink}
      >
        <div className={classes.headerCard}>
          <div className={classes.headerCardContent}>
            {prod?.field_value?.product?.field_value?.menu_logo?.field_value && (
              <img
                src={prod?.field_value?.product?.field_value?.menu_logo?.field_value?.src}
                alt={prod?.field_value?.product?.field_value?.menu_logo?.field_value?.alt}
                className={classes.cardLogo}
              />
            )}
            {prod?.field_value?.product?.field_value?.menu_up_title?.field_value && (
              <Chip
                label={prod?.field_value?.product?.field_value?.menu_up_title?.field_value}
                size="small"
                className={classes.cardChip}
              />
            )}
            <FluidImage
              className={classes.imgContainer}
              {...prod?.field_value?.product?.field_value?.menu_image?.field_value}
            />
          </div>
        </div>
        <Text className={classes.cardTextDescription}>
          {prod?.field_value?.product?.field_value?.menu_title?.field_value}
        </Text>
      </Link>
    </div>
  );

  const RecommendationCard = ({field}) => (
    <div className={classes.recommendationCardContainer}>
      <Link
        className={classes.recommendationCardLink}
        values={field.recommendation.field_value[0].field_value.cta.field_value}
        type={field.recommendation.field_value[0].field_value.cta.field_type}
        onClick={() =>
          GTMEvent(
            info,
            'home_new-arrivals',
            'click_button',
            `cta_${field.recommendation.field_value[0].field_value.cta.field_value.label || field.recommendation.field_value[0].field_value.cta.field_value.title}`
          )
        }
        aria-label={`View ${field.recommendation.field_value[0].field_value.cta.field_value.label || field.recommendation.field_value[0].field_value.cta.field_value.title || 'product'}`}
      />
      <div className={classes.recommendationCardHeader}>
        {!field.recommendation.field_value[0].field_value?.image_foreground?.field_value ?
          <>
            <FluidImage className={classes.imgBackground}
                        {...field.recommendation.field_value[0]?.field_value.product?.field_value?.list_box_v2_image_half_width?.field_value}
            />
            <FluidImage className={classes.imgForeground}
                        {...field.recommendation.field_value[0].field_value.product.field_value?.list_box_v2_image_foreground_50?.field_value}
            />
          </> :
          <FluidImage className={classes.imgForegroundSingle}
                      {...(field.recommendation.field_value[0].field_value?.image_foreground?.field_value)}
          />
        }
      </div>
      <div className={classes.recommendationCardBody}>
        <Text className={classes.productPriceReference}>
          {field.recommendation.field_value[0].field_value?.discover_now?.field_value}
        </Text>
        <Text className={classes.productTitle}>
          {field.recommendation.field_value[0].field_value.product?.field_value?.list_box_v2_title?.field_value}
        </Text>
      </div>
    </div>
  );

  const MegaMenuContent = ({field}) => {
    const [isSliderActive, setIsSliderActive] = useState(false);
    const [isClient, setIsClient] = useState(false);

    const menuContentRef = useRef(null);
    const paginationRef = useRef(null);

    const hasMenuLinks = field?.item?.field_value?.length > 1;
    const hasRecommendation = !!field?.recommendation?.field_value[0].field_value?.product?.field_value;

    let maxCards;
    if (hasMenuLinks && hasRecommendation) maxCards = 3;
    else if (!hasMenuLinks && hasRecommendation) maxCards = 4;
    else if (hasMenuLinks && !hasRecommendation) maxCards = 5;
    else maxCards = 6;

    const visibleProducts = field.product?.field_value.slice(0, maxCards) || [];

    const checkSliderState = useCallback(() => {
      if (!menuContentRef.current) return;
      const contentWidth = menuContentRef.current.scrollWidth;
      if (maxCards === 6) setIsSliderActive(contentWidth < 1330);
      else if (maxCards === 3) setIsSliderActive(contentWidth < 1120);
      else if (maxCards === 4) setIsSliderActive(contentWidth < 1330);
      else if (maxCards === 5) setIsSliderActive(contentWidth < 1120);
    }, [maxCards]);

    useEffect(() => {
      setIsClient(true);
      const animationFrameId = requestAnimationFrame(checkSliderState);
      window.addEventListener('resize', checkSliderState);

      return () => {
        cancelAnimationFrame(animationFrameId);
        window.removeEventListener('resize', checkSliderState);
      };
    }, [checkSliderState]);

    const renderContent = (isSliderContext = false) => (
      <>
        {visibleProducts.map((prod, productIndex) => {
          const card = <ProductCard key={productIndex} prod={prod}/>;
          return isSliderContext ? <SwiperSlide style={{width: '210px'}}>{card}</SwiperSlide> : card;
        })}
        {hasRecommendation && (() => {
          const recommendation = <RecommendationCard field={field}/>;
          return isSliderContext ?
            <SwiperSlide style={{width: '380px'}}>{recommendation}</SwiperSlide> : recommendation;
        })()}
      </>
    );

    return (
      <div className={classes.megaMenuContainer}>
        {(field?.product?.field_value || field?.recommendation?.field_value) &&
          <div className={classes.megaMenuWrapper}>
            {hasMenuLinks && (<div className={classes.megaMenuListContainer}>
              <div className={classes.megaMenuListWrapper}>
                {field?.item?.field_value && field?.item?.field_value.slice(0, -1).map((item, itemIndex) => {
                  if (item?.field_value?.cta?.field_value?.style === 'icon') {
                    return (<Link
                      key={itemIndex}
                      href={item?.field_value?.cta?.field_value?.href}
                      className={classes.ctaDesktop}
                      onClick={() => GTMEvent(info, 'header', 'click_link', item?.field_value?.cta?.field_value?.label)}
                    >
                      <span>{item?.field_value?.cta?.field_value?.label}</span>
                      <Icon name="arrow_right"/>
                    </Link>);
                  } else {
                    return (<Link
                      key={itemIndex}
                      href={item?.field_value?.cta?.field_value?.href}
                      className={classes.fieldMenuDesktop}
                      onClick={() => GTMEvent(info, 'header', 'click_link', item?.field_value?.cta?.field_value?.label)}
                    >
                      {item?.field_value?.cta?.field_value?.label}
                    </Link>);
                  }
                })}
              </div>
            </div>)}
            <div className={classes.menuProductContainer}>
              {isSliderActive && isClient ? (<div className={classes.sliderContainer}>
                <Swiper
                  modules={[Navigation, Pagination]}
                  pagination={{
                    el: `.${classes.megaMenuPagination}`,
                    clickable: true,
                    bulletClass: 'swiper-pagination-bullet',
                    bulletActiveClass: 'swiper-pagination-bullet-active',
                  }}
                  slidesPerView={'auto'}
                  spaceBetween={16}
                  className={classes.megaMenuSwiper}
                >
                  {renderContent(true)}
                </Swiper>
              </div>) : (<div className={classes.cardAndRecommendationContainer} ref={menuContentRef}>
                {renderContent(false)}
              </div>)}
            </div>
          </div>}
        {/*{isSliderActive && isClient && <div className={classes.megaMenuPagination} ref={paginationRef}></div>}*/}
        {field?.item?.field_value && field?.item?.field_value.length > 0 && (
          <div className={classes.megaMenuFooter}>
            <Link
              href={field.item.field_value[field.item.field_value.length - 1]?.field_value?.cta?.field_value?.href}
              className={classes.megaMenuFooterLink}
              onClick={() => GTMEvent(info, 'header', 'click_link', field.item.field_value[field.item.field_value.length - 1]?.field_value?.cta?.field_value?.label)}
            >
              {field.item.field_value[field.item.field_value.length - 1]?.field_value?.cta?.field_value?.label}
              <Icon className={classes.packCardArrow} name="right_orange"/>
            </Link>
          </div>
        )}
      </div>
    );
  };

  // Handle drawer open/close
  const handleDrawerOpen = () => {
    GTMEvent('header', 'menu_mobile', 'open')
    setMenuMobile(true)
  }

  const handleDrawerClose = () => {
    GTMEvent('header', 'menu_mobile', 'close')
    setMenuMobile(false)
  }

  // Handle scroll for sticky header
  const handleScroll = useCallback(() => {
    const threshold = 50;
    const start = threshold * 2;
    const friction = 0.3;

    if (barRef.current) {
      const needSticky = window.scrollY > threshold;

      if (needSticky && !sticky) {
        setSticky(true);
        const animate = () => {
          const pos = Math.min(-start + window.scrollY * friction, 0);
          barRef.current.style.transform = `translate3d(0,${pos}px,0)`;

          if (window.scrollY > threshold) {
            requestAnimationFrame(animate);
          }
        };

        requestAnimationFrame(animate);
      } else if (!needSticky && sticky) {
        setSticky(false);

        const animate = () => {
          barRef.current.style.transform = `translate3d(0,0,0)`;
        };

        requestAnimationFrame(animate);
      }
    }
  }, [sticky]);

  // Menu desktop category handling
  const categoryMenu = (categoryField, height, index) => {
    setIsCategoryOpen({open: true, index: index})
    setCategory(categoryField)
    setHeightCategory(height)
  }

  // Filter products by tag
  const filterClientProducts = filter => {
    setProducts(
      serverProducts.filter(product => {
        if (filter === '' || product?.tags?.includes(filter)) return true
        return false
      })
    )
  }

  const handleItemsMenuClick = (index) => {
    if (openItemsMenu.includes(index)) {
      setOpenItemsMenu(openItemsMenu.filter(item => item !== index))
    } else {
      setOpenItemsMenu([...openItemsMenu, index])
    }
    GTMEvent('header', 'menu_mobile_category', `${index}`)
  }

  // Search functionality
  const handleChange = e => {
    setText(e.target.value)
  }

  // Debounce function for search
  function useDebounce(cb, delay) {
    const [debounceValue, setDebounceValue] = useState(cb)
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebounceValue(cb)
      }, delay)

      return () => {
        clearTimeout(handler)
      }
    }, [cb, delay])
    return debounceValue
  }

  const debounceValue = useDebounce(text, 700)

  // getSearchResults
  const getSearchResults = (page) => {
    if (isSearchLoading) {
      return Promise.resolve();
    }

    setIsSearchLoading(true);

    return axios
      .get(
        `${process.env.GATSBY_SEARCH_PRODUCTS}?${
          text ? `q=${text}` : ''
        }&country=${info?.country || repo?.seo?.country}&page=${page || 1}`
      )
      .then(res => {
        const productsObj = res.data.data?.products || {};
        const productsArray = Object.keys(productsObj).map(key => ({
          id: key,
          ...productsObj[key]
        }));

        return processSearchResults({
          products: productsArray,
          paginator: res.data.data?.paginator,
          brands: res.data.data?.brands || [],
          tags: res.data.data?.tags || []
        });
      })
      .catch(function (thrown) {
        if (!axios.isCancel(thrown)) {
          console.error('Request Error', thrown);
        }
        return thrown;
      })
      .finally(() => {
        setIsSearchLoading(false);
      });
  };

  const processSearchResults = (data) => {
    if (data.paginator.page === 1) {
      setServerProducts(data.products);
      setProducts(data.products);
    } else {
      setServerProducts(prev => [...prev, ...data.products]);
      setProducts(prev => [...prev, ...data.products]);
    }

    setProductsPagination({
      page: data.paginator.page,
      has_more_pages: data.paginator.has_more_pages,
      total_pages: data.paginator.last_page
    });

    setTags(data.tags);

    return data;
  };

  const openCountryModal = () => {
    setShowOverlay(true)
  }

  // Initial setup
  useEffect(() => {
    async function fetchData() {
      setWrapper(await getKeys({login: 'header.login.cta'}));
    }

    fetchData();
  }, []);

  // Fetch countries
  useEffect(() => {
    axios
      .get(`${process.env.GATSBY_COUNTRIES_URL}`)
      .then(res => {
        setAllCountries(res?.data?.data || [])
      })
      .catch(err => console.error('Error fetching countries:', err))

    // Set document country attribute
    if (info?.country) {
      document.body.setAttribute('country', info.country)
    }
  }, [info?.country, repo?.seo?.country])

  // Check for visitor country
  useEffect(() => {
    const cookieDifferentCountry = 'De_Agostini_different_country'
    const cookieVisitorCountry = 'De_Agostini_visitor_country'
    const cookieVisitorCountryCode = 'De_Agostini_country_code'

    if (
      !cookie.getRealCookie(cookieDifferentCountry) ||
      !cookie.getRealCookie(cookieVisitorCountry) ||
      !cookie.getRealCookie(cookieVisitorCountryCode)
    ) {
      axios
        .get(process.env.GATSBY_COUNTRY_LOOKUP_URL)
        .then(res => {
          const countryCode = res.data.country_code
          const countryName = res.data.country
          const currentCountryCode = detectAlpha2Country().toUpperCase() === 'UK'
            ? 'GB'
            : detectAlpha2Country().toUpperCase()

          const isDifferent = countryCode !== currentCountryCode
          setDifferentCountry(isDifferent)
          setCountrySelected(countryName)

          // Save cookies
          cookie.setRealCookie(cookieDifferentCountry, '1', 1)
          cookie.setRealCookie(cookieVisitorCountry, countryName, 1)
          cookie.setRealCookie(cookieVisitorCountryCode, countryCode, 1)
        })
        .catch(function (thrown) {
          console.error('Request Error', thrown)
        })
    } else {
      let visitorCountry = cookie.getRealCookie(cookieVisitorCountry)
      let visitorCountryCode = cookie.getRealCookie(cookieVisitorCountryCode)
      let currentCountryCode = detectAlpha2Country().toUpperCase() === 'UK'
        ? 'GB'
        : detectAlpha2Country().toUpperCase()

      setDifferentCountry(visitorCountryCode !== currentCountryCode)
      setCountrySelected(visitorCountry)
    }
  }, [])

  // Update height information for layout
  useEffect(() => {
    let diff = 0
    let topbar = 0
    let navbar = 0
    let total = 0

    if (differentCountryRef.current) {
      diff = differentCountryRef.current.clientHeight || 0
    }
    if (topbarRef.current) {
      topbar = topbarRef.current.clientHeight || 0
    }
    if (navbarRef.current) {
      navbar = navbarRef.current.clientHeight || 0
    }
    if (barRef.current) {
      total = barRef.current.clientHeight || 0
    }

    dispatch({
      type: 'getHeaderInfo',
      payload: {
        differentCountry: diff,
        topbar: topbar,
        navbar: navbar,
        total: total,
      },
    })
  }, [dispatch, differentCountryRef.current, topbarRef.current, navbarRef.current, barRef.current])

  // Set up scroll listener
  useEffect(() => {
    if (barRef.current && repo?.sticky) {
      handleScroll();
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, repo?.sticky, barRef.current]);

  // Handle body overflow
  useEffect(() => {
    if (openSearch || isMenuMobileOpened) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [openSearch, isMenuMobileOpened]);

  // Search effect
  useEffect(() => {
    if (text.length >= 3) {
      setOpenSearch(true)
      setTagSelected('')
      getSearchResults(1)
    } else {
      setServerProducts([])
      setProducts([])
      setTags([])
      setOpenSearch(false)
    }
  }, [debounceValue])

  // Newsletter popup effect
  useEffect(() => {
    if (detectAlpha2Country().toUpperCase() === 'US') {
      const timer = setTimeout(() => {
        if (repo?.template_label === 'Homepage' && !newsletter?.value) {
          if (!cookie.getRealCookie('newsletterPopup')) {
            cookie.setRealCookie(
              'newsletterPopup',
              JSON.stringify({
                howMany: 0,
                perDay: 0,
                date: new Date(),
              }),
              90
            )
          }

          const popupCookie = JSON.parse(cookie.getRealCookie('newsletterPopup') || '{}')
          if (new Date(popupCookie.date).getDate() !== new Date().getDate()) {
            popupCookie.howMany += 1
            popupCookie.perDay = 0
            popupCookie.date = new Date()
          }

          if (
            popupCookie.perDay === 0 &&
            new Date().getDate() === new Date(popupCookie.date).getDate() &&
            popupCookie.howMany <= 10
          ) {
            GTMEvent(info, 'form_newsletter_popup', 'view')
            setIsOpen(true)
            popupCookie.perDay += 1

            cookie.setRealCookie(
              'newsletterPopup',
              JSON.stringify(popupCookie),
              90
            )
          }
        }
      }, 30000)
      return () => clearTimeout(timer)
    }
  }, [newsletter?.value, repo?.template_label, info])

  // Fetch newsletter content
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_ENDPOINT_SETTINGS + detectAlpha2Country())
      .then(response => {
        let newsContent = response?.data?.data?.content[detectAlpha2Country()]
        let newsRepository = newsContent?.[0]?.data?.newsletter?.repository
        setNews(newsRepository || {})
      })
      .catch(err => console.error('Error fetching newsletter content:', err))
  }, [])

  return (
    <EqualHeight timeout={50} animationSpeed={0}>
      {/* Newsletter modal popup */}
      <Modal open={modalIsOpen} onClose={() => setIsOpen(false)}>
        <Box className={classes.newsletterPopupContainer} display="flex" alignItems="center">
          <Box className={classes.newsPopContent}>
            <Icon
              onClick={() => setIsOpen(false)}
              className={classes.iconClosePop}
              name="close"
            />
            <Box p={2}>
              <img
                className={classes.newspopDesktop}
                alt="Newsletter popup desktop version"
                src={newspopDesktop}
              />
              <img
                className={classes.newspopMobile}
                alt="Newsletter popup mobile version"
                src={newspopMobile}
              />
            </Box>
            <Box p={3}>
              <Text variant="titleL" weight="bold">{news?.title?.field_value}</Text>
              <Text variant="bodyM" className="mt-2">{news?.subtitle?.field_value}</Text>

              <NewsletterSubscriptionModule
                title={news?.title?.field_value}
                subtitle={news?.subtitle?.field_value}
                messages={{
                  success: news?.message_success?.field_value,
                  error: news?.message_error?.field_value,
                }}
                buttonText={news?.button_text?.field_value}
                privacyInfo={
                  news?.privacy_info ||
                  news?.privacy_info?.field_value
                }
                noSpaces={true}
              />
            </Box>
          </Box>
        </Box>
      </Modal>

      <EqualHeightElement name="announcement">
        {/* Header announcements */}
        {HeaderAnnouncement && HeaderAnnouncement.map((bar, index) => (
          <Announcement
            key={index}
            ref={index === 0 ? topbarRef : null}
            className={classes.announcement}
            icon={bar.icon?.field_value}
            cta={bar.cta?.field_value}
          >
            {bar.text?.field_value}
          </Announcement>
        ))}
      </EqualHeightElement>

      {/* Country selection overlay */}
      {allCountries && (
        <ChooseCountryOverlay
          open={showOverlay}
          onClose={() => setShowOverlay(false)}
          countries={allCountries}
        />
      )}

      {/* Different country message */}
      {differentCountry && (
        <div className={classes.differentCountry} ref={differentCountryRef}>
          It seems that you are in {countrySelected}. <br/>
          <a href="#" onClick={openCountryModal}>
            Would you like to change country?
          </a>
        </div>
      )}

      {/* Main Header Bar */}
      <AppBarUI
        position="absolute"
        ref={barRef}
        className={clsx(
          classes.appBar,
          differentCountry && !sticky ? classes.appBarTop : null,
          {
            [info?.black_header === '1' || repo?.black ? classes.stickyBlack : classes.sticky]: sticky,
          }
        )}
      >
        <div className={classes.announcementSpacer}>
          <EqualHeightElement
            timeout={0}
            placeholder
            name="announcement"
          />
        </div>

        {/* Desktop Header */}
        <Hidden smDown implementation="css">
          <Toolbar
            className={clsx(
              classes.toolbar,
              differentCountry && !sticky ? classes.toolbarMarginTop : ''
            )}
            ref={navbarRef}
          >
            {/* Breadcrumbs */}
            {!sticky && breadcrumbs && breadcrumbs.length > 0 && (
              <div className={classes.breadcrumbs}>
                {breadcrumbs.map((breadcrumb, index) => (
                  <span key={index}>
                    {breadcrumb?.field_value?.href ? (
                      <Link
                        href={breadcrumb?.field_value.href}
                        className="text-decoration-none"
                        onClick={() => GTMEvent(
                          info,
                          'header',
                          'click_link',
                          breadcrumb?.field_value?.title
                        )}
                      >
                        {breadcrumb?.field_value?.title}
                      </Link>
                    ) : (
                      breadcrumb?.field_value?.title
                    )}
                  </span>
                ))}
              </div>
            )}

            {/* Logo */}
            {repo?.template_label === 'Homepage' ? (
              <h1 className={classes.logo}>
                <Logo
                  id="header-logo"
                  dark={(sticky || repo?.dark || info?.dark_header === '1') && !repo?.black && info?.black_header !== '1'}
                  templateLabel={templateLabel}
                  onClick={() => GTMEvent('header', 'click_link', 'Home')}
                />
              </h1>
            ) : (
              <div className={classes.logo}>
                <Logo
                  id="header-logo"
                  dark={(sticky || repo?.dark || info?.dark_header === '1') && !repo?.black && info?.black_header !== '1'}
                  templateLabel={templateLabel}
                  onClick={() => GTMEvent('header', 'click_link', 'Home')}
                />
              </div>
            )}

            {/* Right section with menu categories, search and login */}
            <div className={classes.desktopRightSection}>
              {/* Menu categories */}
              <div className="d-flex align-items-center">
                {HeaderMenuItem && HeaderMenuItem.map((element, index) => (
                  <Link
                    key={index}
                    href={element?.cta?.field_value?.href}
                    className="text-decoration-none mr-3"
                    id={`dropdownTargetHeight${index}`}
                    ref={el => menuRefs.current[index] = el}
                    onMouseEnter={() => {
                      if (isCategoryOpen.index !== index) {
                        const rect = menuRefs.current[index]?.getBoundingClientRect();
                        if (rect) {
                          categoryMenu(
                            element?.cta?.field_value?.label,
                            rect.left + (rect.width / 2.5),
                            index
                          );
                        }
                      }
                    }}
                  >
                    <Text
                      className={classes.menuCategory}
                      variant="title"
                      weight="bold"
                      color={
                        (sticky || repo?.dark || info?.dark_header === '1') && !repo?.black && info?.black_header !== '1'
                          ? 'textPrimary'
                          : 'textTertiary'
                      }
                    >
                      {element?.cta?.field_value?.label}
                    </Text>
                  </Link>
                ))}
              </div>

              {/* Search box when active */}
              <div
                className={clsx(
                  searchMobileIsOpen
                    ? classes.search_section_mobile_active
                    : classes.search_section_mobile
                )}
              >
                <div className="container w-100 h-100">
                  <div className="row h-100">
                    <div className="col-24 h-100">
                      <div className="px-2 h-100 d-flex justify-content-between align-items-center gap-4">
                        <Icon
                          className={classes.iconSearch}
                          name="search-white"
                        />
                        <form className="form-search-mobile">
                          <input
                            className={classes.search_input}
                            placeholder={t('search.label')}
                            value={text}
                            autoFocus
                            onChange={handleChange}
                          />
                        </form>
                        <Icon
                          className={classes.iconSearch}
                          name="close"
                          onClick={() => {
                            setSearchMobile(false)
                            setOpenSearch(false)
                            setText('')
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search icon */}
              <Icon
                className={classes.iconSearch}
                name="search-white"
                id="search-input-desktop"
                onClick={() => {
                  setSearchMobile(true)
                  GTMEvent('Header', 'click_button', 'search open')
                }}
              />

              {/* Login button */}
              <LoginButton
                onClick={() => {
                  window.location.href = `/${detectAlpha2Country()}/myarea`
                }}
                title={wrapper?.login}
                id="login-button"
                color={`${
                  (sticky || repo?.dark || info?.dark_header === '1') && !repo?.black && info?.black_header !== '1'
                    ? 'black'
                    : 'white'
                }`}
              />
            </div>
          </Toolbar>
        </Hidden>

        {/* Mobile Header */}
        <Hidden mdUp implementation="css">
          <Toolbar
            className={clsx(
              classes.toolbar,
              differentCountry && !sticky ? classes.toolbarMarginTop : ''
            )}
            ref={navbarRef}
          >
            {/* Mobile menu trigger */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              id="menu-button"
              onClick={handleDrawerOpen}
            >
              <Icon
                className={classes.icon}
                name={`${
                  (sticky || repo?.dark || info?.dark_header === '1') && !repo?.black && info?.black_header !== '1'
                    ? 'menu_purple'
                    : 'menu_white'
                }`}
              />
            </IconButton>

            {/* Logo */}
            {repo?.template_label === 'Homepage' ? (
              <p className={classes.logo}>
                <Logo
                  id="header-logo"
                  dark={(sticky || repo?.dark || info?.dark_header === '1') && !repo?.black && info?.black_header !== '1'}
                  templateLabel={templateLabel}
                  onClick={() => GTMEvent('header', 'click_link', 'Home')}
                />
              </p>
            ) : (
              <div className={classes.logo}>
                <Logo
                  id="header-logo"
                  dark={(sticky || repo?.dark || info?.dark_header === '1') && !repo?.black && info?.black_header !== '1'}
                  templateLabel={templateLabel}
                  onClick={() => GTMEvent('header', 'click_link', 'Home')}
                />
              </div>
            )}

            {/* Search box when active */}
            <div
              className={clsx(
                searchMobileIsOpen
                  ? classes.search_section_mobile_active
                  : classes.search_section_mobile
              )}
            >
              <div className="container w-100 h-100">
                <div className="row h-100">
                  <div className="col-24 h-100">
                    <div className="px-2 h-100 d-flex justify-content-between align-items-center gap-4">
                      <Icon
                        className={classes.iconSearch}
                        name="search-white"
                      />
                      <form className="form-search-mobile">
                        <input
                          className={classes.search_input}
                          id="search-input-mobile"
                          placeholder={t('search.label')}
                          value={text}
                          autoFocus
                          onChange={handleChange}
                        />
                      </form>
                      <Icon
                        className={classes.iconSearch}
                        name="close"
                        onClick={() => {
                          setSearchMobile(false)
                          setOpenSearch(false)
                          setText('')
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search icon */}
            <Icon
              className={classes.iconSearchMobile}
              name="search-white"
              onClick={() => {
                setSearchMobile(true)
                document.getElementById('search-input-mobile').focus()
                GTMEvent('Header', 'click_button', 'search open')
              }}
            />

            {/* Login button */}
            <LoginButton
              onClick={() => {
                window.location.href = `/${detectAlpha2Country()}/myarea`
              }}
              id="login-button"
              title={wrapper?.login}
              color={`${
                (sticky || repo?.dark || info?.dark_header === '1') && !repo?.black && info?.black_header !== '1'
                  ? 'black'
                  : 'white'
              }`}
            />
          </Toolbar>
        </Hidden>

        {/* Search results */}
        {openSearch && (
          <div className={clsx(classes.bgGrey, 'header_search calc-h-menu bg-grey01')}>
            {/* Search filters */}
            {tags && tags.length > 0 && (
              <div className="container px-2">
                <div className="row">
                  <div className="col-24">
                    <div className={classes.filterContainer}>
                      {['', ...tags].map((tag, index) => (
                        <label
                          key={index}
                          className={clsx(
                            classes.filterLabel,
                            tagSelected === tag && classes.filterLabelChecked
                          )}
                        >
                          {tag || wrapper.viewAll}
                          <input
                            type="radio"
                            name="tags"
                            value={tag}
                            checked={tagSelected === tag}
                            onChange={() => {
                              setTagSelected(tag)
                              filterClientProducts(tag)
                            }}
                            style={{display: 'none'}}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Product cards with InfiniteScroll */}
            <div className="mb-10" id="scrollableDiv">
              <div className="container px-2 pb-5">
                <InfiniteScroll
                  dataLength={products.length}
                  next={() => getSearchResults(productsPagination.page + 1)}
                  hasMore={productsPagination.has_more_pages}
                  scrollableTarget="scrollableDiv"
                  loader={<div className="text-center p-3">{t('search.loading')}</div>}
                >
                  <div className="row d-flex w-100 f-wrap">
                    {products.length === 0 ? (
                      <div className="col-100 d-flex justify-content-center pt-64 fontPoppins">
                        {t('search.no_issue_search', {name: text})}
                      </div>
                    ) : (
                      products.map((product, index) => (
                        <CardProductV2
                          key={`product-${product.id || index}`}
                          type={products.length === 1 ? '' : 'half'}
                          onClick={() =>
                            GTMEvent(
                              info,
                              'header',
                              'click_product',
                              product?.list_box_v2?.list_box_v2_title?.field_value
                            )
                          }
                        >
                          {{
                            ...product,
                          }}
                        </CardProductV2>
                      ))
                    )}
                  </div>
                </InfiniteScroll>
              </div>
            </div>
          </div>
        )}

        {/* Desktop mega menu */}
        <div className={classes.menuDesktop}>
          {HeaderMenuItem && HeaderMenuItem.map((field, index) => {
            if (category === field?.cta?.field_value?.label) {
              return (
                <div
                  key={index}
                  className={clsx(
                    classes.headerMenuDesktop,
                    isCategoryOpen.open ? classes.headerMenuDesktopOpen : classes.headerMenuDesktopClose
                  )}
                >
                  {/* Triangle pointer */}
                  <span
                    className={classes.dropdownSelectorTriangle}
                    style={{
                      left: heightCategory + 'px',
                    }}
                  />
                  <MegaMenuContent field={field}/>
                </div>
              )
            }
            return null
          })}

          {isCategoryOpen.open && (
            <div
              className={classes.overlay}
              onMouseEnter={() => {
                setIsCategoryOpen({open: false, index: null})
              }}
            />
          )}
        </div>
      </AppBarUI>

      {/* Mobile menu drawer */}
      <Drawer
        className={classes.drawer}
        anchor="left"
        open={isMenuMobileOpened}
        style={{zIndex: 999999}}
        PaperProps={{
          id: 'menu-container',
        }}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={8} align="left">
              <div className={classes.logo}>
                <Logo
                  dark={true}
                  onClick={() => GTMEvent('header', 'click_link', 'Home')}
                />
              </div>
            </Grid>
            <Grid item xs={4} align="right">
              <IconButton
                className={classes.closeButton}
                onClick={handleDrawerClose}
              >
                <Icon className={classes.closeIcon} name="close"/>
              </IconButton>
            </Grid>
          </Grid>
        </div>

        <List className={classes.list}>
          {/* Menu items with dropdown */}
          {HeaderMenuItem && HeaderMenuItem.map((element, index) => {
            if (element?.item || element?.product) {
              return (
                <div key={index}>
                  <div
                    className={classes.listItem}
                    onClick={() => handleItemsMenuClick(index)}
                  >
                    <div className={classes.containerCategory}>
                      <span className={classes.categoryLink}>
                        {element?.cta?.field_value?.label}
                      </span>
                      <Icon
                        className={classes.goIcon}
                        name="select_menu_mobile"
                        style={{
                          transform: `rotate(${
                            openItemsMenu.includes(index) ? 180 : 0
                          }deg)`,
                        }}
                      />
                    </div>
                  </div>

                  {openItemsMenu.includes(index) && (
                    <div className={classes.listItemWrapper}>
                      {/* Product links */}
                      {element?.product && element?.product?.field_value &&
                        element?.product?.field_value.map((item, itemIndex) => (
                          <ListItem key={`product-${itemIndex}`} className={classes.nestedListItem}>
                            <Link
                              href={item?.field_value?.product?.field_value?.menu_cta?.field_value?.href}
                              className="text-decoration-none w-100"
                              onClick={() => GTMEvent(
                                info,
                                'header',
                                'click_product',
                                item?.field_value?.product?.field_value?.menu_title?.field_value
                              )}
                            >
                              <Text variant="bodyL">
                                {item?.field_value?.product?.field_value?.menu_short_title?.field_value ||
                                  item?.field_value?.product?.field_value?.menu_title?.field_value}
                              </Text>
                            </Link>
                          </ListItem>
                        ))
                      }

                      {/* General links */}
                      {element?.item && element?.item?.field_value &&
                        element?.item?.field_value.map((item, itemIndex) => (
                          <ListItem key={`link-${itemIndex}`} className={classes.nestedListItem}>
                            <Link
                              href={item?.field_value?.cta?.field_value?.href}
                              className="text-decoration-none d-flex justify-content-between align-items-center w-100"
                              onClick={() => GTMEvent(
                                info,
                                'header',
                                'click_link',
                                item?.field_value?.cta?.field_value?.label
                              )}
                            >
                              <Text variant="bodyL">
                                {item?.field_value?.cta?.field_value?.label}
                              </Text>
                              {item?.field_value?.cta?.field_value?.style === 'icon' && (
                                <Icon name="arrow_right"/>
                              )}
                            </Link>
                          </ListItem>
                        ))
                      }
                    </div>
                  )}
                </div>
              )
            } else if (element?.cta?.field_value) {
              return (
                <ListItem key={index} className={classes.listItem}>
                  <Link
                    className={classes.pageLink}
                    href={element?.cta?.field_value?.href}
                    onClick={() => GTMEvent(
                      info,
                      'header',
                      'click_link',
                      element?.cta?.field_value?.title || element?.cta?.field_value?.label
                    )}
                  >
                    <span>{element?.cta?.field_value?.title || element?.cta?.field_value?.label}</span>
                  </Link>
                </ListItem>
              )
            }
            return null;
          })}

          {/* Footer links */}
          {HeaderServices && HeaderServices[0]?.mobileFooter?.field_value && (
            <Box mt={4} p={2} borderTop="1px solid rgba(0,0,0,0.1)">
              {HeaderServices[0].mobileFooter.field_value.map((footerField, index) => (
                <Link
                  key={index}
                  href={footerField?.field_value?.cta?.field_value?.href}
                  className="text-decoration-none d-block mb-2"
                  onClick={() => GTMEvent(
                    info,
                    'header',
                    'click_link',
                    footerField?.field_value?.cta?.field_value?.label
                  )}
                >
                  <Text variant="bodyM">
                    {footerField?.field_value?.cta?.field_value?.label}
                  </Text>
                </Link>
              ))}
            </Box>
          )}
        </List>
      </Drawer>
    </EqualHeight>
  )
}

export default HeaderV2Module